import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	AdminConnexRouteConfigurationRow,
	AdminXpressConnexService,
	AppDataService,
	ConfigService,
	GetAdminConnexRouteSlotsRequest
} from 'app/services';
import { MenuItem, MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { ConnexRouteEditDrawerComponent } from './connex-route-edit-drawer.component';
import { Utils } from 'app/utils';
import { MenuPermissionActionEnum } from 'app/models';

export type ConnexRouteConfigurationTabId = 'routes' | 'assigning-drivers';

@Component({
	selector: 'connex-route-configuration',
	templateUrl: './connex-route-configuration.component.html',
	styleUrls: ['./connex-route-configuration.component.scss', '../../../../styles/paginator.scss']
})
export class ConnexRouteConfigurationComponent implements OnInit, OnDestroy {
	private static readonly pesoFmt = new Intl.NumberFormat('en-PH', {
		maximumFractionDigits: 0,
		minimumFractionDigits: 0
	});

	@Input()
	public menuPermissionActive!: string;

	@ViewChild('routeDrawer') routeDrawer!: ConnexRouteEditDrawerComponent;

	public readonly emptyIllustrationSrc =
		'/XpressApp/assets/images/connex/connex-route-configuration-empty.png';

	public readonly configTabItems: MenuItem[] = [
		{
			id: 'routes',
			label: 'Routes',
			icon: 'pi pi-map',
			command: () => this.setActiveConfigTab('routes')
		},
		{
			id: 'assigning-drivers',
			label: 'Assigning Drivers',
			icon: 'pi pi-users',
			command: () => this.setActiveConfigTab('assigning-drivers')
		}
	];

	public activeConfigTab: MenuItem = this.configTabItems[0];
	public activeConfigTabId: ConnexRouteConfigurationTabId = 'routes';

	public hasLoaded = false;
	public loading = false;
	public totalRecords = 0;
	public routes: AdminConnexRouteConfigurationRow[] = [];
	public readonly rows = 10;

	public first = 0;
	private lastSortField = 'RouteName';
	private lastSortOrder = 1;

	/** Строка фильтров под заголовками (как во вкладке Bookings). */
	public slotHeaderFilters = {
		routeName: '',
		direction: '',
		pickup: '',
		dropoff: '',
		slot: '',
		booking: '',
		walkIn: '',
		status: '',
		updatedAt: ''
	};

	/**
	 * После ответа с TotalRecords > 0 остаётся true, пока «полная» выборка без фильтров не вернёт 0.
	 * Нужен, чтобы при очистке фильтра не мигал экран «нет маршрутов», пока totalRecords ещё от прошлого запроса.
	 */
	private connexRoutesWereLoadedNonEmpty = false;

	public hasActiveSlotFilters(): boolean {
		const f = this.slotHeaderFilters;
		return (
			!!f.routeName?.trim() ||
			!!f.direction?.trim() ||
			!!f.pickup?.trim() ||
			!!f.dropoff?.trim() ||
			!!f.slot?.trim() ||
			!!f.booking?.trim() ||
			!!f.walkIn?.trim() ||
			!!f.status?.trim() ||
			!!f.updatedAt?.trim()
		);
	}

	/** Таблица с caption: есть строки, активен фильтр, или ждём перезагрузку после очистки фильтра (без мигания empty). */
	public showRouteTableShell(): boolean {
		return (
			this.hasLoaded &&
			(this.totalRecords > 0 || this.hasActiveSlotFilters() || this.connexRoutesWereLoadedNonEmpty)
		);
	}

	private filterDebounceTimer: number | undefined;

	/**
	 * Высота области скролла тела таблицы. Режим scrollHeight="flex" в PrimeNG
	 * заставляет строки растягиваться на всю свободную высоту при малом числе строк.
	 */
	public readonly tableBodyScrollHeight = 'calc(100vh - 320px)';

	/** После первой загрузки из ngOnInit таблица монтируется и PrimeNG шлёт лишний onLazyLoad — его один раз пропускаем. Не ставить skip после сортировки/пагинации. */
	private skipNextLazyLoad = false;
	private pendingInitialTableLazyLoadSkip = true;

	constructor(
		public configService: ConfigService,
		private appData: AppDataService,
		private adminConnexService: AdminXpressConnexService,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		void this.loadRoutes({
			first: 0,
			rows: this.rows,
			sortField: 'RouteName',
			sortOrder: 1
		});
	}

	ngOnDestroy(): void {
		if (this.filterDebounceTimer) {
			window.clearTimeout(this.filterDebounceTimer);
		}
	}

	public setActiveConfigTab(tabId: ConnexRouteConfigurationTabId): void {
		this.activeConfigTabId = tabId;
		const item = this.configTabItems.find((t) => t.id === tabId);
		if (item) {
			this.activeConfigTab = item;
		}
	}

	public get canCreateRoute(): boolean {
		return !!this.menuPermissionActive
			&& this.appData.hasPermission(this.menuPermissionActive, MenuPermissionActionEnum.Create);
	}

	public get canUpdateRoute(): boolean {
		return !!this.menuPermissionActive
			&& this.appData.hasPermission(this.menuPermissionActive, MenuPermissionActionEnum.Update);
	}

	public onConfigTabChange(item: MenuItem): void {
		const tabId = (item?.id as ConnexRouteConfigurationTabId) ?? 'routes';
		this.setActiveConfigTab(tabId);
	}

	onCreateRoute(): void {
		if (!this.canCreateRoute) {
			return;
		}
		void this.routeDrawer?.openCreate();
	}

	onViewRoute(routeId: number): void {
		void this.routeDrawer?.openView(routeId);
	}

	onEditRoute(routeId: number): void {
		if (!this.canUpdateRoute) {
			return;
		}
		void this.routeDrawer?.openEdit(routeId);
	}

	onRouteDrawerSaved(): void {
		void this.loadRoutes({
			first: this.first,
			rows: this.rows,
			sortField: this.lastSortField,
			sortOrder: this.lastSortOrder
		});
	}

	/** Debounced reload при вводе в фильтрах (как handleFilterChange в Bookings). */
	public handleHeaderFilterChange(): void {
		if (this.filterDebounceTimer) {
			window.clearTimeout(this.filterDebounceTimer);
		}
		this.filterDebounceTimer = window.setTimeout(() => {
			this.filterDebounceTimer = undefined;
			this.first = 0;
			void this.loadRoutes({
				first: 0,
				rows: this.rows,
				sortField: this.lastSortField,
				sortOrder: this.lastSortOrder
			});
		}, 400);
	}

	directionCountLabel(n: number): string {
		if (n === 1) {
			return '1 Direction';
		}
		return `${n} Directions`;
	}

	slotCountLabel(n: number): string {
		return `${n} Slot(s)`;
	}

	fareRangeLabel(row: AdminConnexRouteConfigurationRow): string {
		const lo = ConnexRouteConfigurationComponent.pesoFmt.format(row.FareRangeMin);
		const hi = ConnexRouteConfigurationComponent.pesoFmt.format(row.FareRangeMax);
		if (row.FareRangeMin === row.FareRangeMax) {
			return `₱ ${lo}`;
		}
		return `₱ ${lo} - ₱ ${hi}`;
	}

	bookingLabel(row: AdminConnexRouteConfigurationRow): string {
		const a = row.BookingWindowMinMinutes;
		const b = row.BookingWindowMaxMinutes;
		if (a === b) {
			return this.formatBookingMinutes(a);
		}
		return `${this.formatBookingMinutes(a)} - ${this.formatBookingMinutes(b)}`;
	}

	private formatBookingMinutes(minutes: number): string {
		if (minutes >= 60 && minutes % 60 === 0) {
			return `${minutes / 60} hrs`;
		}
		if (minutes >= 60) {
			const rounded = Math.round((minutes / 60) * 10) / 10;
			return `${rounded} hrs`;
		}
		return `${minutes} min`;
	}

	walkInLabel(row: AdminConnexRouteConfigurationRow): string {
		const lo = ConnexRouteConfigurationComponent.pesoFmt.format(row.PremiumFareMin);
		if (row.PremiumFareMin === row.PremiumFareMax) {
			return `₱ ${lo}`;
		}
		const hi = ConnexRouteConfigurationComponent.pesoFmt.format(row.PremiumFareMax);
		return `₱ ${lo} - ₱ ${hi}`;
	}

	updatedLabel(row: AdminConnexRouteConfigurationRow): string {
		if (!row.LastUpdatedAt) {
			return 'N/A';
		}

		return Utils.formatUtcRelativeTimeAgo(row.LastUpdatedAt);
	}

	updatedByLabel(row: AdminConnexRouteConfigurationRow): string {
		const updatedBy = (row.LastUpdatedByName ?? '').trim();
		return updatedBy || 'N/A';
	}

	/** Тег статуса: Inactive — danger; Active — success; Draft — secondary (серый). */
	public connexRouteStatusSeverity(status: string | null | undefined): 'danger' | 'success' | 'warning' | 'secondary' {
		const s = (status ?? '').trim().toLowerCase();
		if (s === 'inactive') {
			return 'danger';
		}
		if (s === 'active') {
			return 'success';
		}
		if (s === 'draft') {
			return 'secondary';
		}
		return 'secondary';
	}

	onRouteTableLazyLoad(event: TableLazyLoadEvent): void {
		if (this.skipNextLazyLoad) {
			this.skipNextLazyLoad = false;
			return;
		}
		const sf = event.sortField as string | undefined;
		if (sf) {
			this.lastSortField = sf;
		}
		const sortOrder = event.sortOrder;
		if (sortOrder === 1 || sortOrder === -1) {
			this.lastSortOrder = sortOrder;
		}
		void this.loadRoutes(event);
	}

	private trimOrNull(s: string): string | undefined {
		const v = (s ?? '').trim();
		return v.length > 0 ? v : undefined;
	}

	private buildRequestBody(event: TableLazyLoadEvent): GetAdminConnexRouteSlotsRequest {
		const sortField = (event.sortField as string) || this.lastSortField || 'RouteName';
		const so = event.sortOrder;
		const sortOrder = so === 1 || so === -1 ? so : this.lastSortOrder;

		return {
			SearchName: '',
			Skip: event.first ?? 0,
			Take: event.rows ?? this.rows,
			SortField: sortField,
			SortOrder: sortOrder,
			HeaderColumns: [],
			DataColumns: [],
			RouteNameFilter: this.trimOrNull(this.slotHeaderFilters.routeName),
			DirectionFilter: this.trimOrNull(this.slotHeaderFilters.direction),
			PickupZoneNameFilter: this.trimOrNull(this.slotHeaderFilters.pickup),
			DropoffZoneNameFilter: this.trimOrNull(this.slotHeaderFilters.dropoff),
			SlotFilter: this.trimOrNull(this.slotHeaderFilters.slot),
			BookingWindowMinutesFilter: this.trimOrNull(this.slotHeaderFilters.booking),
			PremiumFareAmountFilter: this.trimOrNull(this.slotHeaderFilters.walkIn),
			StatusFilter: this.trimOrNull(this.slotHeaderFilters.status),
			UpdatedAtFilter: this.trimOrNull(this.slotHeaderFilters.updatedAt)
		};
	}

	private async loadRoutes(event: TableLazyLoadEvent): Promise<void> {
		this.loading = true;
		const body = this.buildRequestBody(event);

		try {
			const res = await this.adminConnexService.getConnexRouteSlots(body, this.messageService);
			this.hasLoaded = true;
			if (res && res.Success === false) {
				this.routes = [];
				this.totalRecords = 0;
				this.connexRoutesWereLoadedNonEmpty = false;
				this.pendingInitialTableLazyLoadSkip = false;
				return;
			}
			const total = res?.TotalRecords ?? 0;
			this.totalRecords = total;
			this.routes = res?.Items ?? [];

			if (total > 0) {
				this.connexRoutesWereLoadedNonEmpty = true;
			} else if (!this.hasActiveSlotFilters()) {
				this.connexRoutesWereLoadedNonEmpty = false;
			}
			if (this.pendingInitialTableLazyLoadSkip && this.totalRecords > 0) {
				this.skipNextLazyLoad = true;
				this.pendingInitialTableLazyLoadSkip = false;
			} else if (this.pendingInitialTableLazyLoadSkip) {
				this.pendingInitialTableLazyLoadSkip = false;
			}
		} finally {
			this.loading = false;
		}
	}
}
